import React from "react";
import { Link } from "react-router-dom";
import PageHero from "@/components/PageHero";
import Section from "@/components/Section";
import NaturalNextSteps from "@/components/NaturalNextSteps";
import TopImage from "@/components/TopImage";
import AsideImage from "@/components/AsideImage";
import BodyText from "@/components/BodyText";
import RichText from "@/components/RichText";
import useContent from "@/hooks/useContent";
import useSeo from "@/hooks/useSeo";
import { IMG } from "@/lib/images";

export default function FourLevels() {
  const t = useContent("four_levels");
  useSeo("four_levels", {
    title: "The Four Levels Experience — Quantum Results",
    description:
      "A self-guided exploration to help you gain a new perspective on how you experience your life.",
  });
  return (
    <div data-testid="page-four-levels">
      <TopImage src={IMG.fourLevels.hero.src} alt={IMG.fourLevels.hero.alt} credit={IMG.fourLevels.hero.credit} testId="four-levels-top-image" />
      <PageHero
        eyebrow={t("hero_eyebrow", "The Four Levels Experience")}
        title={t("hero_title", <>Have you ever felt like you've changed… but your life hasn't fully <span className="text-brand-blue">caught up</span>?</>)}
        lede={t("hero_lede", "Sometimes life changes around us. Sometimes we change. And sometimes, without fully realizing it, the way we experience life begins to change as well.")}
        testId="four-levels-hero"
      />

      <Section testId="fl-invitation">
        <BodyText
          className=""
          value={t.body(
            "invitation_body",
            `The Four Levels Experience is a simple, self-guided exploration designed to help you gain a new perspective on how you experience your life.

It isn't about giving you more information.

It's about helping you notice something that may have been there all along.

Sometimes a new perspective is all it takes to begin seeing familiar circumstances in a completely different way.`
          )}
        />
      </Section>

      <section className="bg-brand-paper/60 border-y border-brand-rule/60">
        <Section
          eyebrow={t("section2_eyebrow", "Recognition")}
          title={t("section2_title", "You may already recognize these moments…")}
          testId="fl-recognition"
        >
          <BodyText
            className=""
            value={t.body(
              "section2_body",
              `Have you ever noticed that the same life can feel completely different from one day to the next?

Some days, everything feels difficult.

Other days, you find yourself responding with greater confidence, creativity, or peace — even though your circumstances haven't changed.

Perhaps you've experienced moments when life seemed to flow naturally, followed by times when everything felt like a struggle again.

Most people assume those experiences are random.

**The Four Levels Experience invites you to consider another possibility. What if there is a simple pattern behind the way you experience your life?**`
            )}
          />
        </Section>
      </section>

      <Section
        eyebrow={t("section3_eyebrow", "A different way to understand your experience")}
        title={t("section3_title", "A simple framework. A different perspective.")}
        testId="fl-framework"
      >
        <BodyText
          className=""
          value={t.body(
            "section3_body",
            `The Four Levels Experience is built around a simple observation.

Although our circumstances may change, the way we experience those circumstances also changes.

Over time, I began to notice that people often describe these experiences in remarkably similar ways.

The Four Levels offers a simple framework for recognizing those patterns — not to place people in a category, but to help them better understand what they're experiencing in the moment.

As you begin recognizing those patterns, something interesting often happens. You stop asking, *"Why is this happening to me?"* and begin asking, *"What am I noticing about the way I'm experiencing this?"*

Sometimes that small shift in perspective opens the door to greater clarity, more intentional choices, and a very different experience of life.`
          )}
        />
      </Section>

      <section className="bg-brand-paper/60 border-y border-brand-rule/60">
        <div className="container-narrow py-20 sm:py-24 grid gap-12 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.15fr)] items-center">
          <AsideImage
            src={IMG.fourLevels.framework.src}
            alt={IMG.fourLevels.framework.alt}
            aspect="aspect-[4/5]"
            testId="four-levels-framework-image"
          />
          <div className="max-w-xl">
            <RichText as="div" className="eyebrow mb-5" value={t("experience_eyebrow", "What you'll experience")} />
            <RichText
              as="h2"
              className="display text-[30px] sm:text-[40px] lg:text-[46px] mb-8"
              value={t("experience_title", "An experience designed for reflection, not information.")}
            />
            <BodyText
              value={t.body(
                "experience_body",
                `The Four Levels Experience is designed to be completed at your own pace.

Through a series of brief conversations and guided reflections, you'll be invited to observe your own experiences from a different perspective.

There are no tests to pass. No right or wrong answers. No pressure to reach a particular conclusion.

Instead, you'll simply be encouraged to notice what resonates, reflect on your own experiences, and allow your own insights to emerge naturally.

Many people finish the experience with something more valuable than another idea. They leave with a different way of seeing themselves and the life they're already living.`
              )}
            />
          </div>
        </div>
      </section>

      <Section
        eyebrow={t("section4_eyebrow", "Begin the journey")}
        title={t("section4_title", "Every new perspective begins with a first step.")}
        testId="fl-begin"
      >
        <BodyText
          className=""
          value={t.body(
            "section4_body",
            `If something you've read here resonates with you, perhaps it's time to explore your own experience through a different lens.

The Four Levels Experience isn't about becoming someone different. It's about recognizing more clearly how you're already experiencing your life — and discovering what new possibilities may become visible as your perspective changes.

You don't need to have everything figured out before you begin. Curiosity is enough.

**Sometimes one new perspective becomes the beginning of an entirely different journey.**`
          )}
        />
        <div className="mt-8 flex flex-wrap gap-4">
          <Link
            to="/four-levels/enroll"
            data-testid="four-levels-begin-cta"
            className="inline-flex items-center gap-2 rounded-full bg-brand-ink px-6 py-3 text-brand-cream text-[14px] tracking-wide hover:bg-brand-blue transition-colors"
          >
            Begin the Four Levels Experience <span aria-hidden>→</span>
          </Link>
        </div>
      </Section>

      <NaturalNextSteps
        note={t("closing_note", "A few adjacent places worth exploring.")}
        items={[
          { to: "/pattern-reveal", label: "Pattern Reveal — patterns quietly shaping your results" },
          { to: "/weekly-quantum-edge", label: "Weekly Quantum Edge — an ongoing rhythm" },
          { to: "/about", label: "About Dan Hoover" },
        ]}
      />
    </div>
  );
}
